import { InfoIcon } from '@sharedCR/coreDesignSystem/icons';
import { compose } from 'redux';

import { YEAR_MAX_LENGTH } from 'constants/fieldLengths';
import { FieldInputType } from 'constants/fields';
import { FieldType } from 'constants/formGenerator';
import { ChattelHomeCondition, PurposeOfChattel } from 'constants/loanApplication';
import {
  boolBaseStatusOptions,
  chattelHomeConditionOptions,
  chattelValueSourceOptions,
  homeSectionsOptions,
} from 'constants/options';
import { fillConfigFieldsWithKeys, getOptionLabelByValue } from 'utils/loanManagement';
import { getChattelYearBuiltField } from 'utils/validation';

import { PropertyAndLoanTestIds } from '../../../../PropertyAndLoan.testIds';
import {
  customIconStyles,
  customIconWrapperStyles,
  customTitleStyles,
  customTitleWrapperStyles,
} from '../MainContent.styles';

const groupTitleWithOffsetWrapperStyles = { ...customTitleWrapperStyles, marginTop: '24px' };

// LEN-204: a chattel refinance is priced on a home-only appraisal, and a used (or repossessed) home on an
// appraisal or NADA book value, so either needs a value to take the LTV on. A new home is priced on its contract.
const HOME_VALUE_REQUIRED_CONDITIONS = [ChattelHomeCondition.USED, ChattelHomeCondition.REPO];

const chattelHomeDetailsConfigFields = {
  make: {
    fieldId: 'make',
    type: FieldType.INPUT,
    nameInModel: 'make',
    inputProps: {
      placeholder: 'Make',
      label: 'Make',
      disabled: false,
      required: false,
      dataTestId: PropertyAndLoanTestIds.CHATTEL_HOME_DETAILS_FORM_MAKE_INPUT,
    },
  },
  model: {
    fieldId: 'model',
    type: FieldType.INPUT,
    nameInModel: 'model',
    inputProps: {
      placeholder: 'Model',
      label: 'Model',
      disabled: false,
      required: false,
      dataTestId: PropertyAndLoanTestIds.CHATTEL_HOME_DETAILS_FORM_MODEL_INPUT,
    },
  },
  yearBuilt: {
    fieldId: 'yearBuilt',
    type: FieldType.INPUT,
    nameInModel: 'yearBuilt',
    inputProps: {
      type: FieldInputType.INT,
      placeholder: 'Year',
      label: 'Year',
      disabled: false,
      required: false,
      maxLength: YEAR_MAX_LENGTH,
      customSchema: getChattelYearBuiltField,
      dataTestId: PropertyAndLoanTestIds.CHATTEL_HOME_DETAILS_FORM_YEAR_BUILT_INPUT,
    },
  },
  condition: {
    fieldId: 'condition',
    type: FieldType.SELECT,
    nameInModel: 'condition',
    inputProps: {
      options: chattelHomeConditionOptions,
      placeholder: 'New / Used',
      label: 'New / Used',
      disabled: false,
      required: false,
      dataTestId: PropertyAndLoanTestIds.CHATTEL_HOME_DETAILS_FORM_NEW_USED_SELECT,
    },
  },
  estimatedCurrentValue: {
    fieldId: 'estimatedCurrentValue',
    type: FieldType.INPUT,
    nameInModel: 'estimatedCurrentValue',
    inputProps: {
      type: FieldInputType.CURRENCY,
      decimalScale: 2,
      placeholder: 'Home Value',
      label: 'Home Value (Appraisal / NADA)',
      disabled: false,
      required: false,
      minValue: 0,
      dataTestId: PropertyAndLoanTestIds.CHATTEL_HOME_DETAILS_FORM_HOME_VALUE_INPUT,
    },
  },
  valueSource: {
    fieldId: 'valueSource',
    type: FieldType.SELECT,
    nameInModel: 'valueSource',
    inputProps: {
      options: chattelValueSourceOptions,
      getOptionLabel: getOptionLabelByValue(chattelValueSourceOptions),
      placeholder: 'Value Source',
      label: 'Value Source',
      disabled: false,
      required: false,
      dataTestId: PropertyAndLoanTestIds.CHATTEL_HOME_DETAILS_FORM_VALUE_SOURCE_SELECT,
    },
  },
  homeType: {
    fieldId: 'homeType',
    type: FieldType.SELECT,
    nameInModel: 'homeType',
    inputProps: {
      options: homeSectionsOptions,
      getOptionLabel: getOptionLabelByValue(homeSectionsOptions),
      placeholder: 'Sections',
      label: 'Sections',
      disabled: false,
      required: false,
      dataTestId: PropertyAndLoanTestIds.CHATTEL_HOME_DETAILS_FORM_SECTIONS_SELECT,
    },
  },
  lotModel: {
    fieldId: 'lotModel',
    type: FieldType.SELECT,
    nameInModel: 'lotModel',
    inputProps: {
      options: boolBaseStatusOptions,
      placeholder: 'Lot Model',
      label: 'Lot Model',
      disabled: false,
      required: false,
      dataTestId: PropertyAndLoanTestIds.CHATTEL_HOME_DETAILS_FORM_LOT_MODEL_SELECT,
    },
  },
  width1: {
    fieldId: 'width1',
    type: FieldType.INPUT,
    nameInModel: 'width1',
    inputProps: {
      type: FieldInputType.FLOAT,
      decimalScale: 2,
      placeholder: 'Sec 1',
      label: 'Width (Sec 1)',
      disabled: false,
      required: false,
      minValue: 0,
      dataTestId: PropertyAndLoanTestIds.CHATTEL_HOME_DETAILS_FORM_WIDTH_INPUT,
    },
  },
  width2: {
    fieldId: 'width2',
    type: FieldType.INPUT,
    nameInModel: 'width2',
    inputProps: {
      type: FieldInputType.FLOAT,
      decimalScale: 2,
      placeholder: 'Sec 2',
      label: 'Width (Sec 2)',
      disabled: false,
      required: false,
      minValue: 0,
      dataTestId: PropertyAndLoanTestIds.CHATTEL_HOME_DETAILS_FORM_WIDTH2_INPUT,
    },
  },
  length1: {
    fieldId: 'length1',
    type: FieldType.INPUT,
    nameInModel: 'length1',
    inputProps: {
      type: FieldInputType.FLOAT,
      decimalScale: 2,
      placeholder: 'Sec 1',
      label: 'Length (Sec 1)',
      disabled: false,
      required: false,
      minValue: 0,
      dataTestId: PropertyAndLoanTestIds.CHATTEL_HOME_DETAILS_FORM_LENGTH_INPUT,
    },
  },
  length2: {
    fieldId: 'length2',
    type: FieldType.INPUT,
    nameInModel: 'length2',
    inputProps: {
      type: FieldInputType.FLOAT,
      decimalScale: 2,
      placeholder: 'Sec 2',
      label: 'Length (Sec 2)',
      disabled: false,
      required: false,
      minValue: 0,
      dataTestId: PropertyAndLoanTestIds.CHATTEL_HOME_DETAILS_FORM_LENGTH2_INPUT,
    },
  },
  serialNumber: {
    fieldId: 'serialNumber',
    type: FieldType.INPUT,
    nameInModel: 'serialNumber',
    inputProps: {
      placeholder: 'Serial #',
      label: 'Serial #',
      disabled: false,
      required: false,
      dataTestId: PropertyAndLoanTestIds.CHATTEL_HOME_DETAILS_FORM_SERIAL_NUMBER_INPUT,
    },
  },
  serialNumber2: {
    fieldId: 'serialNumber2',
    type: FieldType.INPUT,
    nameInModel: 'serialNumber2',
    inputProps: {
      placeholder: 'Serial #2',
      label: 'Serial #2',
      disabled: false,
      required: false,
      dataTestId: PropertyAndLoanTestIds.CHATTEL_HOME_DETAILS_FORM_SERIAL_NUMBER2_INPUT,
    },
  },
  serialNumber3: {
    fieldId: 'serialNumber3',
    type: FieldType.INPUT,
    nameInModel: 'serialNumber3',
    inputProps: {
      placeholder: 'Serial #3',
      label: 'Serial #3',
      disabled: false,
      required: false,
      dataTestId: PropertyAndLoanTestIds.CHATTEL_HOME_DETAILS_FORM_SERIAL_NUMBER3_INPUT,
    },
  },
  hudLabelNumber: {
    fieldId: 'hudLabelNumber',
    type: FieldType.INPUT,
    nameInModel: 'hudLabelNumber',
    inputProps: {
      placeholder: 'HUD #',
      label: 'HUD #',
      disabled: false,
      required: false,
      dataTestId: PropertyAndLoanTestIds.CHATTEL_HOME_DETAILS_FORM_HUD_LABEL_NUMBER_INPUT,
    },
  },
  hudLabelNumber2: {
    fieldId: 'hudLabelNumber2',
    type: FieldType.INPUT,
    nameInModel: 'hudLabelNumber2',
    inputProps: {
      placeholder: 'HUD #2',
      label: 'HUD #2',
      disabled: false,
      required: false,
      dataTestId: PropertyAndLoanTestIds.CHATTEL_HOME_DETAILS_FORM_HUD_LABEL_NUMBER2_INPUT,
    },
  },
  hudLabelNumber3: {
    fieldId: 'hudLabelNumber3',
    type: FieldType.INPUT,
    nameInModel: 'hudLabelNumber3',
    inputProps: {
      placeholder: 'HUD #3',
      label: 'HUD #3',
      disabled: false,
      required: false,
      dataTestId: PropertyAndLoanTestIds.CHATTEL_HOME_DETAILS_FORM_HUD_LABEL_NUMBER3_INPUT,
    },
  },
  certOfTitleNumber: {
    fieldId: 'certOfTitleNumber',
    type: FieldType.INPUT,
    nameInModel: 'certOfTitleNumber',
    inputProps: {
      placeholder: 'Cert of Title #',
      label: 'Cert of Title #',
      disabled: false,
      required: false,
      dataTestId: PropertyAndLoanTestIds.CHATTEL_HOME_DETAILS_FORM_CERT_OF_TITLE_INPUT,
    },
  },
  manufacturerName: {
    fieldId: 'manufacturerName',
    type: FieldType.INPUT,
    nameInModel: 'manufacturerName',
    inputProps: {
      placeholder: 'Mfg Name',
      label: 'Mfg Name',
      disabled: false,
      required: false,
      dataTestId: PropertyAndLoanTestIds.CHATTEL_HOME_DETAILS_FORM_MFG_NAME_INPUT,
    },
  },
  manufacturerPhone: {
    fieldId: 'manufacturerPhone',
    type: FieldType.INPUT,
    nameInModel: 'manufacturerPhone',
    inputProps: {
      placeholder: 'Mfg Phone',
      label: 'Mfg Phone',
      disabled: false,
      required: false,
      dataTestId: PropertyAndLoanTestIds.CHATTEL_HOME_DETAILS_FORM_MFG_PHONE_INPUT,
    },
  },
  manufacturerAddress: {
    fieldId: 'manufacturerAddress',
    type: FieldType.INPUT,
    nameInModel: 'manufacturerAddress',
    inputProps: {
      placeholder: 'Mfg Address',
      label: 'Mfg Address',
      disabled: false,
      required: false,
      dataTestId: PropertyAndLoanTestIds.CHATTEL_HOME_DETAILS_FORM_MFG_ADDRESS_INPUT,
    },
  },
  manufacturerCity: {
    fieldId: 'manufacturerCity',
    type: FieldType.INPUT,
    nameInModel: 'manufacturerCity',
    inputProps: {
      placeholder: 'Mfg City',
      label: 'Mfg City',
      disabled: false,
      required: false,
      dataTestId: PropertyAndLoanTestIds.CHATTEL_HOME_DETAILS_FORM_MFG_CITY_INPUT,
    },
  },
  manufacturerState: {
    fieldId: 'manufacturerState',
    type: FieldType.INPUT,
    nameInModel: 'manufacturerState',
    inputProps: {
      placeholder: 'Mfg State',
      label: 'Mfg State',
      disabled: false,
      required: false,
      dataTestId: PropertyAndLoanTestIds.CHATTEL_HOME_DETAILS_FORM_MFG_STATE_INPUT,
    },
  },
  manufacturerZip: {
    fieldId: 'manufacturerZip',
    type: FieldType.INPUT,
    nameInModel: 'manufacturerZip',
    inputProps: {
      placeholder: 'Zip',
      label: 'Zip',
      disabled: false,
      required: false,
      dataTestId: PropertyAndLoanTestIds.CHATTEL_HOME_DETAILS_FORM_MFG_ZIP_INPUT,
    },
  },
};

const getGroupTitle = (label, withOffset) => ({
  label,
  icon: InfoIcon,
  titleStyles: customTitleStyles,
  wrapperStyles: withOffset ? groupTitleWithOffsetWrapperStyles : customTitleWrapperStyles,
  iconWrapperStyles: customIconWrapperStyles,
  iconStyles: customIconStyles,
});

const getHomeValueField = ({ purposeOfChattel, condition }) => {
  const isRefinance = purposeOfChattel === PurposeOfChattel.REFINANCE;
  const label = isRefinance ? 'Appraised Value' : 'Home Value (Appraisal / NADA)';

  return {
    ...chattelHomeDetailsConfigFields.estimatedCurrentValue,
    inputProps: {
      ...chattelHomeDetailsConfigFields.estimatedCurrentValue.inputProps,
      placeholder: label,
      label,
      required: isRefinance || HOME_VALUE_REQUIRED_CONDITIONS.includes(condition),
    },
  };
};

export const getChattelHomeDetailsConfig = compose(
  fillConfigFieldsWithKeys,
  ({ purposeOfChattel = null, condition = null } = {}) => ({
    groups: [
      {
        type: FieldType.GROUP,
        fields: [
          chattelHomeDetailsConfigFields.make,
          chattelHomeDetailsConfigFields.model,
          chattelHomeDetailsConfigFields.yearBuilt,
          chattelHomeDetailsConfigFields.condition,
          getHomeValueField({ purposeOfChattel, condition }),
          chattelHomeDetailsConfigFields.valueSource,
          chattelHomeDetailsConfigFields.homeType,
          chattelHomeDetailsConfigFields.lotModel,
          chattelHomeDetailsConfigFields.width1,
          chattelHomeDetailsConfigFields.width2,
          chattelHomeDetailsConfigFields.length1,
          chattelHomeDetailsConfigFields.length2,
        ],
        key: 'chattel-home-details-identity',
        title: getGroupTitle('Home Details', false),
      },
      {
        type: FieldType.GROUP,
        fields: [
          chattelHomeDetailsConfigFields.serialNumber,
          chattelHomeDetailsConfigFields.serialNumber2,
          chattelHomeDetailsConfigFields.serialNumber3,
          chattelHomeDetailsConfigFields.hudLabelNumber,
          chattelHomeDetailsConfigFields.hudLabelNumber2,
          chattelHomeDetailsConfigFields.hudLabelNumber3,
          chattelHomeDetailsConfigFields.certOfTitleNumber,
        ],
        key: 'chattel-home-details-serial-hud',
        title: getGroupTitle('Serial & HUD Numbers', true),
      },
      {
        type: FieldType.GROUP,
        fields: [
          chattelHomeDetailsConfigFields.manufacturerName,
          chattelHomeDetailsConfigFields.manufacturerPhone,
          chattelHomeDetailsConfigFields.manufacturerAddress,
          chattelHomeDetailsConfigFields.manufacturerCity,
          chattelHomeDetailsConfigFields.manufacturerState,
          chattelHomeDetailsConfigFields.manufacturerZip,
        ],
        key: 'chattel-home-details-manufacturer',
        title: getGroupTitle('Manufacturer', true),
      },
    ],
  }),
);
